
interface ModelDetailsProps {
  model: {
    name: string;
    category: string;
    price: number;
    originalPrice: number;
    rating: number;
    reviews: number;
    inStock: boolean;
    stockCount: number;
    description: string;
    specifications: Record<string, string>;
    features: string[];
    marketplaceLinks?: {
      etsy?: string;
      makerworld?: string;
      printables?: string;
    };
  };
  quantity: number;
  onQuantityChange: (quantity: number) => void;
}

export default function ModelDetails({ model, quantity, onQuantityChange }: ModelDetailsProps) {
  const discount = Math.round(((model.originalPrice - model.price) / model.originalPrice) * 100);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="text-sm text-gray-500 mb-2">{model.category}</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{model.name}</h1>
        
        {/* Rating */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <i 
                key={i} 
                className={`ri-star-${i < Math.floor(model.rating) ? 'fill' : 'line'} text-yellow-400`}
              ></i>
            ))}
            <span className="text-sm text-gray-600 ml-2">
              {model.rating} ({model.reviews} reviews)
            </span>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-center gap-4 mb-6">
          <span className="text-3xl font-bold text-gray-900">${model.price}</span>
          {model.originalPrice > model.price && (
            <>
              <span className="text-xl text-gray-500 line-through">${model.originalPrice}</span>
              <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-medium">
                {discount}% OFF
              </span>
            </>
          )}
        </div>

        {/* Stock Status */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`w-3 h-3 rounded-full ${model.inStock ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className={`text-sm font-medium ${model.inStock ? 'text-green-700' : 'text-red-700'}`}>
            {model.inStock ? `In Stock (${model.stockCount} available)` : 'Out of Stock'}
          </span>
        </div>
      </div>

      {/* Marketplace Links */}
      {model.marketplaceLinks && (
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-store-2-line text-blue-600"></i>
            Available on Marketplaces
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {model.marketplaceLinks.etsy && (
              <a
                href={model.marketplaceLinks.etsy}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white hover:bg-gray-50 p-4 rounded-lg border border-gray-200 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <i className="ri-store-line text-orange-600 text-lg"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Etsy</div>
                  <div className="text-sm text-gray-500">Handmade marketplace</div>
                </div>
                <i className="ri-external-link-line text-gray-400 ml-auto"></i>
              </a>
            )}
            
            {model.marketplaceLinks.makerworld && (
              <a
                href={model.marketplaceLinks.makerworld}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white hover:bg-gray-50 p-4 rounded-lg border border-gray-200 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-3d-view-line text-green-600 text-lg"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">MakerWorld</div>
                  <div className="text-sm text-gray-500">3D printing community</div>
                </div>
                <i className="ri-external-link-line text-gray-400 ml-auto"></i>
              </a>
            )}
            
            {model.marketplaceLinks.printables && (
              <a
                href={model.marketplaceLinks.printables}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white hover:bg-gray-50 p-4 rounded-lg border border-gray-200 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-printer-line text-purple-600 text-lg"></i>
                </div>
                <div>
                  <div className="font-medium text-gray-900">Printables</div>
                  <div className="text-sm text-gray-500">Free 3D models</div>
                </div>
                <i className="ri-external-link-line text-gray-400 ml-auto"></i>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Description */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Description</h3>
        <p className="text-gray-600 leading-relaxed">{model.description}</p>
      </div>

      {/* Features */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Features</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {model.features.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
              <i className="ri-check-line text-green-600"></i>
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {/* Specifications */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Specifications</h3>
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          {Object.entries(model.specifications).map(([key, value]) => (
            <div key={key} className="flex justify-between text-sm">
              <span className="text-gray-600">{key}:</span>
              <span className="font-medium text-gray-900">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quantity and Add to Cart */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Quantity:</label>
          <div className="flex items-center border border-gray-300 rounded-lg">
            <button
              onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
              className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-subtract-line"></i>
            </button>
            <span className="w-12 text-center font-medium">{quantity}</span>
            <button
              onClick={() => onQuantityChange(Math.min(model.stockCount, quantity + 1))}
              className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-add-line"></i>
            </button>
          </div>
        </div>

        <div className="flex gap-4">
          <button 
            disabled={!model.inStock}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-4 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            Add to Cart - ${(model.price * quantity).toFixed(2)}
          </button>
          <button className="w-14 h-14 flex items-center justify-center border-2 border-gray-200 hover:border-blue-600 hover:text-blue-600 rounded-lg transition-colors cursor-pointer">
            <i className="ri-heart-line text-xl"></i>
          </button>
        </div>

        <button className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 px-6 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
          Buy Now
        </button>
      </div>
    </div>
  );
}
